import React, {forwardRef, useEffect, useImperativeHandle, useRef, useState} from "react";
import {Card} from "../../../../../components/card/card.jsx";
import SentryRow from "../../../../../components/template/sentry-row.jsx";
import InputForm from "../../../../../components/input/input-form.jsx";
import InputTextarea from "../../../../../components/input/input-textarea.jsx";
import InputCheckbox from "../../../../../components/input/input-checkbox.jsx";
import InputDatetime from "../../../../../components/input/input-datetime.jsx";
import SelectComponent from "../../../../../components/select/select-component.jsx";
import Loader from "../../../../../components/template/loader.jsx";
import {useQuery} from "@tanstack/react-query";
import projectUtils from "../../../../../utils/project-utils.js";
import DispatchService from "../../../../../services/dispatch/dispatch-service.js";
import InputFile from "../../../../../components/input/input-file.jsx";

const mapMechanicOptions = (list = []) =>
    list.map(station => ({value: station.id, label: station.name}));

const mapItems = (vehicle, fallbackItems = []) => {
    const items = vehicle.maintenanceItems ?? vehicle.items ?? fallbackItems ?? [];
    return items.map(item => ({
        descriptionId: item.id ?? item.descriptionId ?? item.description_id,
        description: item.description ?? item.name,
        checked: false,
        nextKm: "",
        nextDate: "",
    }));
};

const DispatchMaintenance = forwardRef(({dispatchId, initialMileage, finalMileage, garrisonId, isSuggestKm = true, place, showLocation = false, title = true}, ref) => {
    const [rows, setRows] = useState([]);
    const tempKey = useRef(0);
    const nextTempId = () => `mnt-${tempKey.current++}`;

    const {isLoading, data} = useQuery({
        queryKey: ["dispatch-maintenance", dispatchId ?? "", garrisonId ?? ""],
        enabled: !!garrisonId,
        queryFn: async () => {
            const result = await DispatchService.getClosedDispatchMaintenance(dispatchId, garrisonId);
            if (result === "BAD_REQUEST") {
                projectUtils.swalErrorMessage("Error", "Não foi possível carregar dados de manutenção.");
                return {};
            }

            const compositions = result.compositionGarrison ?? [];
            const fallbackItems = result.maintenanceItems ?? [];
            const placeName = place || result.dispatchPlace || "";
            const resolvedMechanic = (result.mechanics ?? []).find(station => String(station.name) === String(placeName))?.id ?? "";

            setRows(compositions.map(item => {
                const conductor = (item.integrants ?? []).find(integrant => integrant.conductor);

                return {
                    tempId: nextTempId(),
                    vehicleId: item.vehicleId ?? item.vehicle?.id ?? item.vehicleModel?.id,
                    vehicleName: item.vehicleName ?? item.vehicle?.name ?? item.vehicleModel?.name ?? item.name,
                    conductorId: conductor?.contingent?.id,
                    conductorName: conductor?.contingent?.namewar ?? conductor?.contingent?.name ?? "",
                    notMaintenance: false,
                    mechanicId: showLocation ? resolvedMechanic : "",
                    kmStart: isSuggestKm ? initialMileage ?? item.initialMileage ?? "" : "",
                    kmEnd: isSuggestKm ? finalMileage ?? "" : "",
                    valuePay: "",
                    description: "",
                    file: null,
                    items: mapItems(item, fallbackItems),
                };
            }));

            return result;
        },
    });

    useEffect(() => {
        if (!isSuggestKm || finalMileage === undefined || finalMileage === null || finalMileage === "") return;
        setRows(prev => prev.map(row => ({...row, kmEnd: finalMileage})));
    }, [finalMileage, isSuggestKm]);

    const mechanicOptions = mapMechanicOptions(data?.mechanics);

    const updateRow = (tempId, partial) =>
        setRows(prev => prev.map(row => row.tempId === tempId ? {...row, ...partial} : row));

    const updateItem = (rowId, descriptionId, partial) =>
        setRows(prev => prev.map(row => row.tempId === rowId
            ? {...row, items: row.items.map(item => item.descriptionId === descriptionId ? {...item, ...partial} : item)}
            : row));

    const hasValue = (value) => value !== undefined && value !== null && String(value).trim() !== "";

    const validate = () => {
        const missingByVehicle = [];

        rows.forEach(row => {
            if (row.notMaintenance) return;

            const missingFields = [];
            if (showLocation && !hasValue(row.mechanicId)) missingFields.push("Oficina");
            if (!hasValue(row.kmStart)) missingFields.push("Km entrada");
            if (!hasValue(row.kmEnd)) missingFields.push("Km saída");
            if (!hasValue(row.description)) missingFields.push("Descrição");

            if (missingFields.length > 0) missingByVehicle.push(`${row.vehicleName || "Viatura"}: ${missingFields.join(", ")}`);
        });

        if (missingByVehicle.length === 0) return true;

        projectUtils.swalMessage(
            "warning",
            "Campos obrigatórios",
            ["Preencha os campos obrigatórios da manutenção:", ...missingByVehicle],
            "Ok"
        );

        return false;
    };

    useImperativeHandle(ref, () => ({
        validate,
        collect: () => {
            const list = [];
            const files = [];
            let fileCount = 1;

            rows.forEach(row => {
                if (row.notMaintenance) return;

                let fileName = null;
                if (row.file) {
                    const dot = row.file.name.lastIndexOf(".");
                    fileName = `${row.file.name.substring(0, dot)}-${fileCount}${row.file.name.substring(dot)}`;
                    files.push({field: "maintenanceFiles", file: row.file.file, name: fileName});
                    fileCount++;
                }

                list.push({
                    vehicleId: row.vehicleId,
                    conductor: row.conductorId,
                    mechanic: showLocation ? undefined : (row.mechanicId || null),
                    mechanics: showLocation ? (row.mechanicId || null) : undefined,
                    valuePay: row.valuePay || null,
                    kmStart: row.kmStart || null,
                    kmEnd: row.kmEnd || null,
                    description: row.description || "",
                    maintenanceItems: row.items.map(item => ({
                        km: item.checked,
                        next_km: item.nextKm !== "" ? parseInt(item.nextKm) : null,
                        next_date: item.nextDate || null,
                        description: item.description,
                        description_id: item.descriptionId != null ? parseInt(item.descriptionId) : null,
                    })),
                    fileName,
                });
            });

            return {list: list.length > 0 ? list : null, files};
        },
    }), [rows, showLocation]);

    return (
        <Card className="border-0!">
            {isLoading && <Loader/>}
            {
                title &&
                <Card.Header>
                    <Card.Title>Manutenção</Card.Title>
                </Card.Header>
            }
            <Card.Body className="flex flex-col gap-4 p-0!">
                {rows.map(row => (
                    <div key={row.tempId} className="flex flex-col gap-3 rounded border border-[#ededed] p-3">
                        <div className="flex items-center gap-4">
                            <span className="font-semibold text-gray-600">
                                {row.vehicleName} {row.conductorName ? `- ${row.conductorName}` : ""}
                            </span>
                            <InputCheckbox
                                id={`notMaintenance-${row.tempId}`}
                                label="Não realizar manutenção"
                                checked={row.notMaintenance}
                                onChange={({target}) => updateRow(row.tempId, {notMaintenance: target.checked})}
                            />
                        </div>

                        {!row.notMaintenance &&
                            <>
                                {showLocation &&
                                    <SentryRow className="items-end">
                                        <SelectComponent
                                            classNameWrapper="col-span-6"
                                            label="Oficina *"
                                            options={mechanicOptions}
                                            value={mechanicOptions.find(option => String(option.value) === String(row.mechanicId)) ?? null}
                                            onChange={(option) => updateRow(row.tempId, {mechanicId: option?.value ?? ""})}
                                        />
                                    </SentryRow>
                                }
                                <SentryRow className="items-end">
                                    <InputForm classNameWrapper="col-span-3" id={`kmStart-${row.tempId}`} label="Km entrada *" integerOnly type="number" value={row.kmStart} onChange={({target}) => updateRow(row.tempId, {kmStart: target.value})}/>
                                    <InputForm classNameWrapper="col-span-3" id={`kmEnd-${row.tempId}`} label="Km saída *" integerOnly type="number" value={row.kmEnd} onChange={({target}) => updateRow(row.tempId, {kmEnd: target.value})}/>
                                    <InputForm classNameWrapper="col-span-3" id={`valuePay-${row.tempId}`} label="Valor pago" type="number" value={row.valuePay} onChange={({target}) => updateRow(row.tempId, {valuePay: target.value})}/>
                                    <div className="col-span-3">
                                        <InputFile
                                            file={row.file}
                                            accept="image/*,application/pdf"
                                            setFile={(file) => updateRow(row.tempId, {file: file ?? null})}
                                            removeFile={() => updateRow(row.tempId, {file: null})}
                                        />
                                    </div>
                                </SentryRow>

                                {row.items.length > 0 &&
                                    <div className="flex flex-col gap-2">
                                        {row.items.map(item => (
                                            <SentryRow key={item.descriptionId} className="items-end">
                                                <div className="col-span-4 flex items-center h-[32px]">
                                                    <InputCheckbox
                                                        id={`item-${row.tempId}-${item.descriptionId}`}
                                                        label={item.description}
                                                        checked={item.checked}
                                                        onChange={({target}) => updateItem(row.tempId, item.descriptionId,
                                                            target.checked ? {checked: true} : {checked: false, nextKm: "", nextDate: ""})}
                                                    />
                                                </div>
                                                <InputForm
                                                    classNameWrapper="col-span-4"
                                                    label="Próximo km"
                                                    integerOnly
                                                    type="number"
                                                    value={item.nextKm}
                                                    disabled={!item.checked}
                                                    onChange={({target}) => updateItem(row.tempId, item.descriptionId, {nextKm: target.value})}
                                                />
                                                <InputDatetime
                                                    classNameWrapper="col-span-4"
                                                    id={`item-date-${row.tempId}-${item.descriptionId}`}
                                                    label="Próxima data"
                                                    dateFormat="L"
                                                    returnDateObj={false}
                                                    disabled={!item.checked}
                                                    selected={item.nextDate}
                                                    onChange={(value) => updateItem(row.tempId, item.descriptionId, {nextDate: value ?? ""})}
                                                />
                                            </SentryRow>
                                        ))}
                                    </div>
                                }

                                <InputTextarea
                                    id={`description-${row.tempId}`}
                                    label="Descrição *"
                                    textAreaClass="h-25!"
                                    value={row.description}
                                    onChange={({target: {value}}) => updateRow(row.tempId, {description: value})}
                                />
                            </>
                        }
                    </div>
                ))}
            </Card.Body>
        </Card>
    );
});

DispatchMaintenance.displayName = "DispatchMaintenance";

export default DispatchMaintenance;
